import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Card,
  Button,
  Spinner,
  Alert,
  Pagination,
  Row,
  Col,
  Badge,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

function Summaries() {
  const [summaries, setSummaries] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Receipt modal states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedShiftReceipts, setSelectedShiftReceipts] = useState([]);
  const [selectedShiftName, setSelectedShiftName] = useState("");
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsError, setReceiptsError] = useState(null);
  const [receiptsPage, setReceiptsPage] = useState(1);
  const [receiptsTotalPages, setReceiptsTotalPages] = useState(1);
  const [receiptsTotalItems, setReceiptsTotalItems] = useState(0);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const token = localStorage.getItem("token");

  const [expiryDate, setExpiryDate] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(0);

  useEffect(() => {
    const storedExpiry = localStorage.getItem("expires_in");
    const storedLastUpdated = localStorage.getItem("last_updated");

    if (storedExpiry) {
      setExpiryDate(new Date(storedExpiry));
    }
    if (storedLastUpdated) {
      setLastUpdated(new Date(storedLastUpdated));
    }
  }, []);

  // Calculate days remaining
  useEffect(() => {
    if (expiryDate) {
      const today = new Date();
      const timeDiff = expiryDate - today;
      const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      setDaysRemaining(days > 0 ? days : 0);
    }
  }, [expiryDate]);

  const fetchSummaries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/summaries?page=${currentPage}&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 401) {
        // Unauthorized - redirect to login
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch summaries");
      }

      const data = await response.json();
      setSummaries(data.summaries);
      setDateRange(data.dateRange);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalItems);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  const fetchReceipts = async (shift_id, page = 1) => {
    setReceiptsLoading(true);
    setReceiptsError(null);
    try {
      const response = await fetch(
        `${API_URL}/receipts?shift_id=${shift_id}&page=${page}&limit=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch receipts");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    } finally {
      setReceiptsLoading(false);
    }
  };
  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleViewReceipts = async (shift_id, shift_name) => {
    setSelectedShiftId(shift_id);
    setSelectedShiftName(shift_name);
    setReceiptsPage(1);
    setShowReceiptModal(true);

    try {
      const data = await fetchReceipts(shift_id, 1);
      setSelectedShiftReceipts(data.receipts);
      setReceiptsTotalPages(data.totalPages);
      setReceiptsTotalItems(data.totalItems);
    } catch (err) {
      setReceiptsError(err.message);
    }
  };

  const handleReceiptsPageChange = async (newPage) => {
    setReceiptsPage(newPage);
    try {
      const data = await fetchReceipts(selectedShiftId, newPage);
      setSelectedShiftReceipts(data.receipts);
      setReceiptsTotalPages(data.totalPages);
      setReceiptsTotalItems(data.totalItems);
    } catch (err) {
      setReceiptsError(err.message);
    }
  };

  const handleCloseModal = () => {
    setShowReceiptModal(false);
    setSelectedShiftReceipts([]);
    setSelectedShiftName("");
    setSelectedShiftId("");
    setReceiptsError(null);
    setReceiptsPage(1);
  };

  useEffect(() => {
    if (!token || !user) {
      navigate("/login");
      return;
    }
    fetchSummaries();
  }, [currentPage]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getPaymentMethodBadge = (method, amount) => {
    if (!amount || amount <= 0) return null;

    let bgColor = "secondary";
    if (method === "cash") bgColor = "success";
    if (method === "card") bgColor = "primary";
    if (method === "mobile_money") bgColor = "info";

    return (
      <Badge bg={bgColor} className="me-1 mb-1">
        {method}: {formatCurrency(amount)}
      </Badge>
    );
  };

  if (loading && summaries.length === 0) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading summaries...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">Sales Summaries</h2>
          {lastUpdated && (
            <small className="text-muted">
              Last synced: {formatDateTime(lastUpdated)}
            </small>
          )}
        </Col>
        <Col xs="auto">
          <div
            className={`rounded p-2 d-flex align-items-center gap-3 ${
              daysRemaining <= 7
                ? "bg-danger bg-opacity-10"
                : daysRemaining <= 30
                  ? "bg-warning bg-opacity-10"
                  : "bg-light"
            }`}
          >
            <div className="d-flex align-items-center gap-2">
              <i
                className={`bi bi-hourglass-split fs-5 ${
                  daysRemaining <= 7
                    ? "text-danger"
                    : daysRemaining <= 30
                      ? "text-warning"
                      : "text-secondary"
                }`}
              ></i>
              <span className="text-muted">Expires:</span>
              <span
                className={`fw-bold ${
                  daysRemaining <= 7
                    ? "text-danger"
                    : daysRemaining <= 30
                      ? "text-warning"
                      : "text-dark"
                }`}
              >
                {expiryDate ? formatDate(expiryDate) : "Not set"}
              </span>
            </div>
            {expiryDate && (
              <>
                <div className="vr"></div>
                <div className="d-flex align-items-center gap-2">
                  <i
                    className={`bi bi-calendar-week fs-5 ${
                      daysRemaining <= 7
                        ? "text-danger"
                        : daysRemaining <= 30
                          ? "text-warning"
                          : "text-info"
                    }`}
                  ></i>
                  <span className="text-muted">in</span>
                  <span
                    className={`fw-bold ${
                      daysRemaining <= 7
                        ? "text-danger"
                        : daysRemaining <= 30
                          ? "text-warning"
                          : "text-primary"
                    }`}
                  >
                    {daysRemaining} {daysRemaining === 1 ? "day" : "days"}
                  </span>
                </div>
                {daysRemaining <= 7 && (
                  <>
                    <div className="vr"></div>
                    <Badge bg="danger" className="animate-pulse">
                      <i className="bi bi-exclamation-triangle-fill me-1"></i>
                      Expiring soon!
                    </Badge>
                  </>
                )}
              </>
            )}
          </div>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={fetchSummaries} disabled={loading}>
            {loading ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <i className="bi bi-arrow-repeat me-1"></i> Refresh
              </>
            )}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {dateRange && (
        <Alert variant="info" className="mb-4">
          Showing summaries from {formatDate(dateRange.from)} to{" "}
          {formatDate(dateRange.to)}
        </Alert>
      )}

      {summaries.length === 0 ? (
        <Alert variant="warning">No summaries found for the last 30 days</Alert>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="d-none d-md-block">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Shift</th>
                  <th>Location</th>
                  <th>Total Sales</th>
                  <th>Transactions</th>
                  <th>Payment Methods</th>
                  <th>Performance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((summary) => (
                  <tr key={summary._id}>
                    <td className="align-middle">
                      {formatDate(summary.summary_date)}
                    </td>
                    <td className="align-middle">{summary.shift_name}</td>
                    <td className="align-middle">
                      <Badge bg="info">{summary.location_name || "N/A"}</Badge>
                    </td>
                    <td className="align-middle fw-bold">
                      {formatCurrency(summary.sales_totals?.total_sales || 0)}
                    </td>
                    <td className="align-middle">
                      {summary.transaction_stats?.total_transactions || 0}
                    </td>

                    <td className="align-middle">
                      {getPaymentMethodBadge(
                        "cash",
                        summary.payment_methods?.cash?.amount,
                      )}
                      {getPaymentMethodBadge(
                        "card",
                        summary.payment_methods?.card?.amount,
                      )}
                      {getPaymentMethodBadge(
                        "mobile_money",
                        summary.payment_methods?.mobile_money?.amount,
                      )}
                    </td>
                    <td className="align-middle">
                      <div>
                        Trans/Hour:{" "}
                        {summary.performance_metrics?.transaction_per_hour?.toFixed(
                          2,
                        ) || 0}
                      </div>
                    </td>
                    <td className="align-middle">
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() =>
                          handleViewReceipts(
                            summary.shift_id,
                            summary.shift_name,
                          )
                        }
                      >
                        View Receipts
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="d-md-none">
            <Row>
              {summaries.map((summary) => (
                <Col xs={12} key={summary._id} className="mb-3">
                  <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>{formatDate(summary.summary_date)}</strong>
                      <Badge bg="secondary">{summary.shift_name}</Badge>
                      <Badge bg="info" className="mt-1 ms-1">
                        {summary.location_name || "N/A"}
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <Row className="mb-2">
                        <Col xs={6}>
                          <small className="text-muted">Total Sales</small>
                          <div className="fw-bold">
                            {formatCurrency(
                              summary.sales_totals?.total_sales || 0,
                            )}
                          </div>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted">Transactions</small>
                          <div className="fw-bold">
                            {summary.transaction_stats?.total_transactions || 0}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <small className="text-muted">Payment Methods</small>
                          <div className="mt-1">
                            {getPaymentMethodBadge(
                              "cash",
                              summary.payment_methods?.cash?.amount,
                              summary.payment_methods?.cash?.percentage,
                            )}
                            {getPaymentMethodBadge(
                              "card",
                              summary.payment_methods?.card?.amount,
                              summary.payment_methods?.card?.percentage,
                            )}
                            {getPaymentMethodBadge(
                              "mobile_money",
                              summary.payment_methods?.mobile_money?.amount,
                              summary.payment_methods?.mobile_money?.percentage,
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                    <Card.Footer className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="text-muted">
                          Trans/Hour:{" "}
                          {summary.performance_metrics?.transaction_per_hour?.toFixed(
                            2,
                          ) || 0}
                        </small>
                        <Badge
                          bg={
                            summary.performance_metrics?.conversion_rate > 0
                              ? "success"
                              : "secondary"
                          }
                          className="ms-2"
                        >
                          {summary.performance_metrics?.conversion_rate || 0}%
                          conv.
                        </Badge>
                      </div>
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() =>
                          handleViewReceipts(
                            summary.shift_id,
                            summary.shift_name,
                          )
                        }
                      >
                        Receipts
                      </Button>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="text-muted">
                Showing page {currentPage} of {totalPages} ({totalItems} total
                items)
              </div>
              <Pagination>
                <Pagination.Prev
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                />
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <Pagination.Item
                      key={i}
                      active={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Pagination.Item>
                  );
                })}
                <Pagination.Next
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      {/* Receipts Modal */}
      <Modal show={showReceiptModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Receipts - {selectedShiftName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {receiptsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading receipts...</p>
            </div>
          ) : receiptsError ? (
            <Alert variant="danger">{receiptsError}</Alert>
          ) : selectedShiftReceipts.length === 0 ? (
            <Alert variant="info">No receipts found for this shift</Alert>
          ) : (
            <>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Receipt #</th>
                    <th>Date</th>
                    <th>Total Amount</th>
                    <th>Customer name</th>

                    <th>Payment Breakdown</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedShiftReceipts.map((receipt) => (
                    <tr key={receipt._id}>
                      <td className="align-middle">
                        <strong>
                          {receipt.receipt_ref || receipt.receipt_id}
                        </strong>
                      </td>
                      <td className="align-middle">
                        {formatDateTime(receipt.receipt_date)}
                      </td>
                      <td className="align-middle fw-bold text-success">
                        {formatCurrency(receipt.total_amount || 0)}
                      </td>
                      <td className="align-middle fw-bold text-success">
                        {receipt.customer_alias || "Walk in"}
                      </td>
                      <td className="align-middle">
                        <div className="d-flex flex-wrap gap-1">
                          {receipt.cash_amount > 0 && (
                            <Badge bg="success" className="me-1">
                              Cash: {formatCurrency(receipt.cash_amount)}
                            </Badge>
                          )}
                          {receipt.card_amount > 0 && (
                            <Badge bg="primary" className="me-1">
                              Card: {formatCurrency(receipt.card_amount)}
                            </Badge>
                          )}
                          {receipt.mobile_money > 0 && (
                            <Badge bg="info" className="me-1">
                              Mobile: {formatCurrency(receipt.mobile_money)}
                            </Badge>
                          )}
                          {receipt.voucher_amount > 0 && (
                            <Badge bg="warning" className="me-1">
                              Voucher: {formatCurrency(receipt.voucher_amount)}
                            </Badge>
                          )}
                          {!receipt.cash_amount &&
                            !receipt.card_amount &&
                            !receipt.mobile_money &&
                            !receipt.voucher_amount && (
                              <Badge bg="secondary">No payment data</Badge>
                            )}
                        </div>
                      </td>
                      <td className="align-middle">
                        {receipt.is_cancelled ? (
                          <Badge bg="danger">Cancelled</Badge>
                        ) : receipt.is_update ? (
                          <Badge bg="warning">Updated</Badge>
                        ) : (
                          <Badge bg="success">Completed</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Optional: Summary Cards for Mobile */}
              {/* <div className="d-md-none">
                {selectedShiftReceipts.map((receipt) => (
                  <Card key={receipt._id} className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <strong>Receipt #{receipt.receipt_ref}</strong>
                      <Badge
                        bg={
                          receipt.is_cancelled
                            ? "danger"
                            : receipt.is_update
                              ? "warning"
                              : "success"
                        }
                      >
                        {receipt.is_cancelled
                          ? "Cancelled"
                          : receipt.is_update
                            ? "Updated"
                            : "Completed"}
                      </Badge>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col xs={6}>
                          <small className="text-muted">Date</small>
                          <div>{formatDateTime(receipt.receipt_date)}</div>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted">Total Amount</small>
                          <div className="fw-bold text-success">
                            {formatCurrency(receipt.total_amount || 0)}
                          </div>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col xs={12}>
                          <small className="text-muted">Payment Details</small>
                          <div className="mt-1">
                            {receipt.cash_amount > 0 && (
                              <div>
                                Cash: {formatCurrency(receipt.cash_amount)}
                              </div>
                            )}
                            {receipt.card_amount > 0 && (
                              <div>
                                Card: {formatCurrency(receipt.card_amount)}
                              </div>
                            )}
                            {receipt.mobile_money > 0 && (
                              <div>
                                Mobile: {formatCurrency(receipt.mobile_money)}
                              </div>
                            )}
                            {receipt.voucher_amount > 0 && (
                              <div>
                                Voucher:{" "}
                                {formatCurrency(receipt.voucher_amount)}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div> */}

              {/* Receipts Pagination */}
              {receiptsTotalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <small className="text-muted">
                    Page {receiptsPage} of {receiptsTotalPages} (
                    {receiptsTotalItems} receipts)
                  </small>
                  <Pagination size="sm">
                    <Pagination.Prev
                      onClick={() => handleReceiptsPageChange(receiptsPage - 1)}
                      disabled={receiptsPage === 1}
                    />
                    <Pagination.Item active>{receiptsPage}</Pagination.Item>
                    <Pagination.Next
                      onClick={() => handleReceiptsPageChange(receiptsPage + 1)}
                      disabled={receiptsPage === receiptsTotalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Summaries;
